import React from 'react';

const TONES = {
  crit: 'text-crit bg-crit-tint',
  high: 'text-high bg-high-tint',
  med: 'text-med bg-med-tint',
  low: 'text-low bg-low-tint',
  ok: 'text-ok bg-ok-tint',
  neutral: 'text-ink bg-low-tint',
  signal: 'text-signal bg-signal-soft',
};

const SEVERITIES = {
  critical: { label: 'Critical', tone: 'crit' },
  high: { label: 'High', tone: 'high' },
  medium: { label: 'Medium', tone: 'med' },
  moderate: { label: 'Medium', tone: 'med' },
  low: { label: 'Low', tone: 'low' },
};

const STATUS_TONES = {
  active: 'neutral',
  open: 'neutral',
  opened: 'neutral',
  new: 'neutral',
  preview: 'neutral',
  pending: 'med',
  'waiting for approval': 'med',
  waiting_for_approval: 'med',
  waiting4user: 'med',
  waiting4agent: 'med',
  created: 'signal',
  creating: 'signal',
  promoting: 'signal',
  'in progress': 'signal',
  inprogress: 'signal',
  creation_failed: 'crit',
  canceled: 'low',
  cancelled: 'low',
  resolved: 'ok',
  fixed: 'ok',
  closed: 'ok',
  done: 'ok',
};

// Returns { label, tone } for any severity string the API sends, or null when empty.
export const normalizeSeverity = (severity) => {
  if (!severity) return null;
  const known = SEVERITIES[String(severity).trim().toLowerCase()];
  return known || { label: String(severity), tone: 'low' };
};

const SEVERITY_RANK = { crit: 4, high: 3, med: 2, low: 1 };

// The most severe of the given severity strings, or null when none are set.
export const highestSeverity = (severities) =>
  severities
    .map(normalizeSeverity)
    .filter(Boolean)
    .reduce((top, s) => (!top || SEVERITY_RANK[s.tone] > SEVERITY_RANK[top.tone] ? s : top), null)?.label ?? null;

export default function Badge({ tone = 'neutral', children, title }) {
  return (
    <span title={title} className={`inline-flex items-center h-5 px-1.5 rounded-badge text-small font-semibold whitespace-nowrap ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function SeverityBadge({ severity }) {
  const normalized = normalizeSeverity(severity);
  if (!normalized) return <span className="text-graphite">Unknown</span>;
  return <Badge tone={normalized.tone}>{normalized.label}</Badge>;
}

const STATUS_LABELS = {
  opened: 'Opened',
  inprogress: 'In progress',
  waiting4user: 'Waiting for user',
  waiting4agent: 'Waiting for agent',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  preview: 'Preview',
  created: 'Created',
  creating: 'Creating in Jotform',
  promoting: 'Opening in Jotform',
  waiting_for_approval: 'Waiting for approval',
  creation_failed: 'Creation failed',
};

export function StatusBadge({ status }) {
  if (!status) return <span className="text-graphite">Unknown</span>;
  const text = String(status).replace(/_/g, ' ');
  const key = text.toLowerCase();
  const tone = STATUS_TONES[key] || 'neutral';
  const label = STATUS_LABELS[key] || text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  return <Badge tone={tone}>{label}</Badge>;
}
